/**
 * @file GovernanceCompliancePage.tsx
 * @description Container page for Governance Dashboard — renders sub-pages via Outlet
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 *
 * Conformed to Spec 197 design blueprint — token-underline sub-navigation tabs
 * (raw grays/hex → design tokens); presentation-only, no logic/route changes.
 * This is a thin tab-shell container: each sub-page renders its own
 * <PageHeader>, so this wrapper intentionally adds no second page header.
 */

import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { ShieldCheck, Users, Globe, Layers, Server } from 'lucide-react';
import { clsx } from 'clsx';

const tabs = [
  { label: 'Tenant Settings', path: 'tenant-settings', icon: ShieldCheck },
  { label: 'Workspace Access', path: 'workspace-access', icon: Users },
  { label: 'Shared Artifacts', path: 'shared-artifacts', icon: Globe },
  { label: 'Data Lineage', path: 'data-lineage', icon: Layers },
  { label: 'Gateways', path: 'gateways', icon: Server },
];

const GovernanceCompliancePage: React.FC = () => {
  const location = useLocation();

  // Redirect /operations/governance to /operations/governance/tenant-settings
  if (location.pathname === '/operations/governance' || location.pathname === '/operations/governance/') {
    return <Navigate to="tenant-settings" replace />;
  }

  return (
    <div>
      {/* Sub-navigation — token underline tabs (design blueprint) */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 sm:px-6">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Governance tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  clsx(
                    '-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                      : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Render active sub-page */}
      <Outlet />
    </div>
  );
};

export default GovernanceCompliancePage;
